/**
 * Compiling the setup form into a campaign premise.
 *
 * The premise is not decoration — it becomes the agent's foundation page and
 * seeds the transcription provider's keyterms, so invented proper nouns
 * survive being spoken aloud. Its exact wording is therefore load-bearing and
 * this is a faithful port of the shipped compiler rather than a rewrite.
 *
 * The "LIVE PLANNING INSTRUCTIONS" block in particular encodes rules that came
 * out of real sessions — especially the starting-location discipline, which
 * exists because the agent kept opening sessions at the destination the party
 * was travelling toward rather than where they actually were.
 */

export interface PlayerDraft {
  playerName: string;
  characterName: string;
  /** Character level, as typed. The only field encounter difficulty is computed
   *  from — budget.py scores entirely from party size and levels — so an empty
   *  one costs real accuracy. Kept as a string because it comes from an input;
   *  parsed on submit. */
  level: string;
  backstory: string;
  playerDetails: string;
}

export interface CampaignDraft {
  slug: string;
  name: string;
  /** Which system this table plays. A slug, because it keys the compiled
   *  system-profile cache server-side. */
  systemKey: string;
  /** BCP-47 primary subtag. Drives transcription and everything the
   *  showrunner writes. */
  language: string;
  /** House rules and corrections. The server treats these as ground truth over
   *  the model's own recollection of the system. */
  systemNotes: string;
  world: string;
  opening: string;
  tone: string;
  throughlineExperience: string;
  gmProfile: string;
  players: PlayerDraft[];
}

export const emptyPlayer = (): PlayerDraft => ({
  playerName: '',
  characterName: '',
  level: '',
  backstory: '',
  playerDetails: '',
});

export const emptyDraft = (): CampaignDraft => ({
  slug: '',
  name: '',
  systemKey: 'dnd5e',
  language: 'en',
  systemNotes: '',
  world: '',
  opening: '',
  tone: '',
  throughlineExperience: '',
  gmProfile: '',
  players: [emptyPlayer()],
});

const line = (label: string, value: string): string =>
  `- ${label}: ${value.trim() || '(not provided)'}`;

export function compilePremise(draft: CampaignDraft): string {
  const players = draft.players.some(hasContent)
    ? draft.players
        .filter(hasContent)
        .map((player, index) =>
          [
            `Player ${index + 1}`,
            line('Player name', player.playerName),
            line('Character name', player.characterName),
            line('Character level', player.level),
            line('Character backstory', player.backstory),
            line('Private player details', player.playerDetails),
          ].join('\n'),
        )
        .join('\n\n')
    : '(No player details provided yet.)';

  return [
    'THROUGHLINE CAMPAIGN SETUP',
    '',
    'Use this setup as first-class planning context. Tune branch type, spotlight pacing, complexity, tone, and callback selection to this specific table. The player and GM notes are private; do not surface them directly to the table.',
    '',
    'CAMPAIGN',
    line('Campaign name', draft.name),
    line('World, setting, and premise', draft.world),
    line('Where the session begins', draft.opening),
    line('Tone and pacing', draft.tone),
    '',
    'PLAYERS AND CHARACTERS',
    players,
    '',
    'GM PROFILE',
    line('Throughline experience', draft.throughlineExperience),
    line('GM strengths, weaknesses, and support needs', draft.gmProfile),
    '',
    'LIVE PLANNING INSTRUCTIONS',
    '- Throughline should adapt to the GM, not the other way around.',
    '- Starting-location discipline: the opening narration and opening checkpoint must begin at the exact location described in "Where the session begins." The broader world/setting field is not the starting location unless the opening field says so.',
    '- If the opening says the characters are heading toward, traveling to, waiting for a shipment from, or bound for a place, they are not there yet. Start where they actually are now.',
    '- Treat GM improvisation as canon as soon as it appears in the transcript.',
    '- If the GM invents a side character, backstory, location detail, joke, or surprising connection, rebuild future checkpoints around it when useful.',
    '- Favor locally plausible details that could belong to a master plan; avoid turning random contradictions into the center of the story unless the table clearly chooses them.',
    '- Use player details to decide when to offer action, mystery, moral debate, comedy, downtime, or spotlight beats.',
  ].join('\n');
}

function hasContent(player: PlayerDraft): boolean {
  return Boolean(
    player.playerName.trim() ||
      player.characterName.trim() ||
      player.backstory.trim() ||
      player.playerDetails.trim(),
  );
}

/** Mirrors the server's slug normalisation so the preview matches what saves. */
export function slugify(value: string): string {
  const chars: string[] = [];
  let previousDash = false;
  for (const char of value.trim().toLowerCase()) {
    if ((char >= 'a' && char <= 'z') || (char >= '0' && char <= '9')) {
      chars.push(char);
      previousDash = false;
    } else if (!previousDash) {
      chars.push('-');
      previousDash = true;
    }
  }
  return chars.join('').replace(/^-+|-+$/g, '').slice(0, 80);
}
